import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  FiMenu, FiX, FiHome, FiPackage, FiShoppingCart, FiShoppingBag,
  FiFileText, FiUsers, FiUserCheck, FiBarChart2, FiSettings,
  FiChevronDown, FiLogOut, FiUser, FiLayers, FiShield, FiMoon, FiSun, FiMonitor, FiDatabase
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import '../styles/Layout.css';

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 992);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { currentUser, userRole, logout, selectedHierarchy } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    setUserMenuOpen(false);
    const result = await logout();
    if (result.success) {
      window.location.href = '/login';
    } else {
      console.error('Logout failed:', result.error);
    }
  };

  const handleProfile = () => {
    setUserMenuOpen(false);
    navigate('/settings');
  };

  const menuItems = [
    { text: 'Dashboard', icon: <FiHome />, path: '/' },
    { text: 'Categories', icon: <FiLayers />, path: '/categories' },
    { text: 'Products', icon: <FiPackage />, path: '/products' },
    { text: 'Stock', icon: <FiPackage />, path: '/stock-management' },
    { text: 'Purchases', icon: <FiShoppingBag />, path: '/purchases' },
    { text: 'Orders', icon: <FiShoppingCart />, path: '/orders' },
    { text: 'Invoices', icon: <FiFileText />, path: '/invoices' },
    { text: 'Customers', icon: <FiUsers />, path: '/customers' },
    { text: 'Employees', icon: <FiUserCheck />, path: '/employees' },
    { text: 'Roles', icon: <FiUserCheck />, path: '/roles' },
    { text: 'Permissions', icon: <FiShield />, path: '/permissions' },
    { text: 'Terminals', icon: <FiMonitor />, path: '/terminals' },
    { text: 'Data Explorer', icon: <FiDatabase />, path: '/data-explorer' },
    { text: 'Reports', icon: <FiBarChart2 />, path: '/reports' },
    { text: 'Settings', icon: <FiSettings />, path: '/settings' },
  ];

  return (
    <div className="layout-container">
      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'show' : 'collapsed'}`}>
        <div className="sidebar-header">
          <div className="d-flex align-items-center">
            {sidebarOpen && (
              <div className="sidebar-brand ms-3">
                <h5 className="mb-0">POS Admin</h5>
              </div>
            )}
          </div>
          <button 
            className="btn btn-link text-white p-0" 
            onClick={toggleSidebar}
          >
            {sidebarOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            <a
              key={item.text}
              href={item.path}
              className={`sidebar-nav-item ${location.pathname === item.path ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(item.path);
              }}
            >
              {item.icon}
              {sidebarOpen && <span>{item.text}</span>}
            </a>
          ))}
        </nav>
      </div>

      {/* Mobile Backdrop */}
      {sidebarOpen && (
        <div 
          className="sidebar-backdrop d-lg-none" 
          onClick={toggleSidebar}
        />
      )}

      {/* Main Content */}
      <div className={`main-content ${sidebarOpen ? '' : 'expanded'}`}>
        {/* Top Navbar */}
        <nav className="navbar navbar-light bg-white border-bottom shadow-sm">
          <div className="container-fluid">
            <button 
              className="btn btn-link text-dark d-lg-none" 
              onClick={toggleSidebar}
            >
              <FiMenu size={24} />
            </button>

            <div className="d-flex align-items-center mx-auto">
              {selectedHierarchy?.outlet && (
                <span className="badge bg-primary rounded-pill px-4 py-2 fs-6">
                  <i className="bi bi-shop me-2"></i>
                  {selectedHierarchy.outlet.name}
                </span>
              )}
            </div>

            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-link text-dark p-2"
                onClick={toggleTheme}
                title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                {isDark ? <FiSun size={20} /> : <FiMoon size={20} />}
              </button>

              <div className="dropdown">
              <button
                className="btn btn-link text-dark d-flex align-items-center"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                <div className="user-avatar me-2">
                  {currentUser?.displayName?.charAt(0).toUpperCase() || 'U'}
                </div>
                <FiChevronDown size={16} />
              </button>

              {userMenuOpen && (
                <>
                  <div 
                    className="dropdown-backdrop" 
                    onClick={() => setUserMenuOpen(false)}
                  ></div>
                  <div className="dropdown-menu dropdown-menu-end show" style={{ position: 'absolute', right: 0 }}>
                    <div className="dropdown-header">
                      <div className="fw-bold">{currentUser?.displayName || 'User'}</div>
                      <small className="text-primary">Role: {userRole || 'User'}</small>
                    </div>
                    
                    {selectedHierarchy && selectedHierarchy.business && (
                      <>
                        <div className="dropdown-divider"></div>
                        <div className="dropdown-header">
                          <div className="fw-bold text-primary">Current Business</div>
                          <small className="text-muted">{selectedHierarchy.business.name || 'Unknown Business'}</small>
                          {selectedHierarchy.outlet && (
                            <small className="d-block text-muted">
                              {selectedHierarchy.outlet.name || 'Unknown Outlet'}
                              {selectedHierarchy.city && ` (${selectedHierarchy.city.name})`}
                            </small>
                          )}
                        </div>
                        <button 
                          className="dropdown-item" 
                          onClick={() => {
                            setUserMenuOpen(false);
                            navigate('/select-business');
                          }}
                        >
                          <i className="bi bi-arrow-repeat me-2"></i>
                          Change Business
                        </button>
                      </>
                    )}
                    
                    <div className="dropdown-divider"></div>
                    <button className="dropdown-item" onClick={handleProfile}>
                      <FiUser className="me-2" />
                      Profile
                    </button>
                    <button className="dropdown-item text-danger" onClick={handleLogout}>
                      <FiLogOut className="me-2" />
                      Logout
                    </button>
                  </div>
                </>
              )}
              </div>
            </div>
          </div>
        </nav>

        {/* Page Content */}
        <div className="content-area">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
